import { useInView } from './useInView';

/**
 * Arms a one-pass `Sheen` to run when its element reaches the viewport.
 *
 * A thin reading of `useInView`, and the reason it is worth its own hook is
 * the one option it changes: `skipAboveFold: false`. The reveals deliberately
 * leave above-the-fold content alone, because hiding and then fading in
 * something already on screen reads as a flash. The sheen has no such problem
 * — it hides nothing, so there is nothing to flash — and it is the element
 * meant to catch the eye, which it cannot do if it silently declines to run
 * on the one viewport where the reader is already looking.
 *
 * Reduced motion needs no handling here: `useInView` reports `static` for
 * those visitors and never arms the observer, so `play` stays false and the
 * heading simply sits there. Like the reveals, the pass runs once and the
 * observer disconnects.
 */
export function useSheenOnEnter<T extends HTMLElement = HTMLDivElement>() {
  const { ref, state } = useInView<T>({
    // A little deeper into the viewport than a reveal waits for. The pass is
    // 1.5s long and the point is that it is seen, so it should not start on
    // the first pixel of a band that is still mostly below the fold.
    rootMargin: '0px 0px -18% 0px',
    skipAboveFold: false,
  });

  return { ref, play: state === 'shown' };
}
